import Head from 'next/head'
import { ProductDetail } from 'features/ECommerce/screens/ProductDetail'
import { prefetchProductDetail } from 'features/ECommerce/api/queries/useFetchProductDetail'
import { GetServerSidePropsContext } from 'next'
import { ProductDetail as ProductDetailType } from 'models/Product'
import { useTranslation } from 'hooks/useTranslation'
import { ProductDetailLegacy } from 'features/ECommerce/screens/ProductDetailLegacy'

type ProductDetailWithConstantSlugs = Omit<ProductDetailType, 'slug'> & {
  slug:
    | '5-lb-beef-patty-recipe'
    | '5-lb-chicken-patty-recipe'
    | '5-lb-venison-patty-recipe'
    | '5-lb-duck-patty-recipe'
    | '5-lb-lamb-patty-recipe'
    | '5-lb-turkey-patty-recipe'
    | 'big-box'
    | 'classic-box'
    | 'small-box'
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const { productId } = ctx.query as { productId: string }

  const { dehydratedState, data } = await prefetchProductDetail(productId)

  if (dehydratedState.queries[0]?.queryKey[1] !== productId) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...dehydratedState,
      metaData: data,
    },
  }
}

export default function ProductDetailPage({
  metaData,
}: {
  metaData: ProductDetailWithConstantSlugs
}) {
  const { t } = useTranslation()

  const { pageTitle, title, description } = {
    pageTitle: t(`e-commerce:detail.product.${metaData.slug}.pageTitle`),
    title: t(`e-commerce:detail.product.${metaData.slug}.title`),
    description: t(`e-commerce:detail.product.${metaData.slug}.description`),
  }

  const canonicalUrl = `https://wefeedraw.com/products/${metaData.slug}`
  const itemPrice = (metaData.price.subtotal / 100).toFixed(2)

  // prettier-ignore
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${title}`,
    url: `https://wefeedraw.com/products/${metaData.slug}`,
    image: `https://wefeedraw.com${metaData.image}`,
    description: `${metaData.description.enUS}`,
    brand: {
      '@type': 'Brand',
      name: 'We Feed Raw'
    },
    offers: {
      '@type': 'Offer',
      url: `https://wefeedraw.com/products/${metaData.slug}`,
      priceCurrency: 'USD',
      price: `${itemPrice}`,
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'We Feed Raw'
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0.00',
          currency: 'USD'
        }
      },
      additionalProperty: {
        '@type': 'PropertyValue',
        name: 'Shipping',
        value: 'Free Shipping'
      }
    }
  };

  const jsonLdString = JSON.stringify(schema)

  return (
    <div>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <div
          dangerouslySetInnerHTML={{
            __html: `<script type="application/ld+json">${jsonLdString}</script>`,
          }}
        />
      </Head>

      <main>
        {[
          '5-lb-chicken-patty-recipe',
          '5-lb-beef-patty-recipe',
          '5-lb-venison-patty-recipe',
          '5-lb-duck-patty-recipe',
          '5-lb-lamb-patty-recipe',
          '5-lb-turkey-patty-recipe',
        ].includes(metaData.slug) ? (
          <ProductDetail />
        ) : (
          <ProductDetailLegacy />
        )}
      </main>
    </div>
  )
}
